<template>
  <VglGeometry   
    :draw-range-start="drawingFrom"
    :draw-range-count="drawingTo - drawingFrom"
  >
    <template #position>
      <VglFloat32Attribute
        :array="points"
        :item-size="3"
      />
    </template>
    <template #color>
      <VglFloat32Attribute
        :array="colors"
        :item-size="3"
      />
    </template>
  </VglGeometry>
</template>

<script>
import * as d3 from 'd3'

import { 
  VglGeometry,
  VglFloat32Attribute,
} from 'vue-gl';

import { getNorm } from '@js/utils/TrajectoryUtil.js';

export default {
  components: {
    VglGeometry,
    VglFloat32Attribute,
  },
  props: {
    trajectory: {
      required: true,
    },
    horizontalOffset: {
      default: 0,
    },
    verticalOffset: {
      default: 0,
    },
    selection: {
    },
    colorPattern: {
      default: 'bilateral',
    },
  },
  computed: {
    startIndex: function(){
      return this.trajectory.positions[0].index ?? 0;
    },
    drawingMax: function(){
      return this.trajectory.positions.length - 1;
    },
    drawingFrom: function(){
      let rtn = this.selection?.from ?? 0;
      rtn = parseInt(rtn) - this.startIndex;
      return rtn < 0 ? 0 : Math.min(this.drawingMax, rtn);
    },
    drawingTo: function(){
      let rtn = this.selection?.to ?? this.drawingMax;
      rtn = parseInt(rtn) - this.startIndex;
      return rtn < 0 ? 0 : Math.min(this.drawingMax, rtn);
    },
    positions: function(){
      const rtn = [];
      this.trajectory.positions.forEach(p => {
        rtn.push(getNorm({
          x: p.x,
          y: p.y,
          z: p.z,
        }));
      });
      return rtn;
    },
    minPosition: function(){
      return Math.min(...this.positions);
    },
    maxPosition: function(){
      return Math.max(...this.positions);
    },
    velocities: function(){
      const rtn = [];
      let previousPoint = {
        x: 0,
        y: 0,
        z: 0
      }
      this.trajectory.positions.forEach(p => {
        rtn.push(getNorm({
          x: p.x - previousPoint.x,
          y: p.y - previousPoint.y,
          z: p.z - previousPoint.z,
        }));

        previousPoint = p;
      });
      return rtn;
    },
    minVelocity: function(){
      return Math.min(...this.velocities);
    },
    maxVelocity: function(){
      return Math.max(...this.velocities);
    },
    accelerations: function(){
      const rtn = [];
      let previousPoint = {
        x: 0,
        y: 0,
        z: 0
      }
      let previousVelocity = {
        x: 0,
        y: 0,
        z: 0
      };
      this.trajectory.positions.forEach(p => {
        const velocity = {
          x: p.x - previousPoint.x,
          y: p.y - previousPoint.y,
          z: p.z - previousPoint.z,
        }

        rtn.push(getNorm({
          x: velocity.x - previousVelocity.x,
          y: velocity.y - previousVelocity.y,
          z: velocity.z - previousVelocity.z,
        }));

        previousPoint = p;
        previousVelocity = velocity;
      });
      return rtn;
    },
    minAcceleration: function(){
      return Math.min(...this.accelerations);
    },
    maxAcceleration: function(){
      return Math.max(...this.accelerations);
    },
    points: function(){
      let rtn = [];
      this.trajectory.positions.forEach(p => {
        rtn.push(p.x - this.horizontalOffset);
        rtn.push(p.y - this.verticalOffset);
        rtn.push(p.z);
      });
      return rtn;
    },
    getColor: function(){
      //MEMO https://wizardace.com/d3v5-scale-chromatic/
      return value => {
        const v = (value - this.colorMinValue)/this.colorValueRange;
        return d3.interpolateSpectral(1 - v);
      }
    },
    colorValueRange: function(){
      switch(this.colorPattern){
        case 'position':
          return this.maxPosition - this.minPosition;
        case 'velocity':
          return this.maxVelocity - this.minVelocity;
        case 'acceleration':
          return this.maxAcceleration - this.minAcceleration;
      }
      return 1;
    },
    colorMinValue: function(){
      switch(this.colorPattern){
        case 'position':
          return this.minPosition;
        case 'velocity':
          return this.minVelocity;
        case 'acceleration':
          return this.minAcceleration;
      }
      return 0;
    },
    colorValues: function(){
      switch(this.colorPattern){
        case 'position':
          return this.positions;
        case 'velocity':
          return this.velocities;
        case 'acceleration':
          return this.accelerations;
      }
      return [];
    },
    colors: function(){
      const rtn = [];
      
      this.colorValues.forEach(v => {
        const color = this.getColor(v).replace('rgb(', '').replace(')', '');
        const values = color.split(',').map(c => parseInt(c)/255);
        rtn.push(values[0]);
        rtn.push(values[1]);
        rtn.push(values[2]);
      });
      return rtn;
    },
  },
}
</script>